import React from "react";
import { useEffect, useRef, useState } from "react";

import { ArrowRight, Brain, Layers, FileText, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import howlyfaceDarkWebp from "@/assets/howlyface-dark-384.webp";
import howlyfaceDark from "@/assets/howlyface-dark.png";
import howlyfaceLightWebp from "@/assets/howlyface-light-384.webp";
import howlyfaceLight from "@/assets/howlyface-light.png";
import { useTheme } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTES } from "@/lib/routes";

const Index = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mascotRef = useRef<HTMLDivElement>(null);

  // Determine which mascot to show based on theme
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const mascotImage = isDark ? howlyfaceDark : howlyfaceLight;
  const mascotImageWebp = isDark ? howlyfaceDarkWebp : howlyfaceLightWebp;

  // Parallax effect for mascot
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const parallaxSpeed = 0.3;
      setParallaxOffset(scrolled * parallaxSpeed);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observers = cardRefs.current.map((card, index) => {
      if (!card) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleCards((prev) => new Set(prev).add(index));
            }
          });
        },
        { threshold: 0.1 },
      );

      observer.observe(card);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Thinking Support",
      description:
        "From complex decisions to daily admin. Get intelligent thinking partners that help you tackle whatever's on your plate - big or small.",
    },
    {
      icon: Layers,
      title: "Strategic Frameworks",
      description:
        "Structure your thinking and offload mental overhead. Save your energy for the work that truly needs your unique perspective.",
    },
    {
      icon: FileText,
      title: "Ready-Made Templates",
      description:
        "Pre-built boards for everything from high-stakes decisions to managing overwhelming task lists. Start with structure, not a blank page.",
    },
    {
      icon: Sparkles,
      title: "Your Second Brain",
      description:
        "Stop carrying everything in your head. Capture context, delegate the routine, and free up your mental bandwidth.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="flex items-center justify-center p-4 md:p-8 min-h-screen">
        <div className="max-w-5xl w-full">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Left side - Mascot */}
            <div className="flex justify-center md:justify-end order-2 md:order-1">
              <div
                ref={mascotRef}
                className=""
                style={{
                  transform: `translateY(${parallaxOffset}px)`,
                  transition: "transform 0.1s ease-out",
                }}
              >
                <picture>
                  <source srcSet={mascotImage} type="image/png" />
                  <img
                    src={mascotImage}
                    alt="AdvisorMuse Mascot welcoming you"
                    className="w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 object-contain hover-scale transition-transform duration-500"
                    width="384"
                    height="384"
                    fetchPriority="high"
                  />
                </picture>
              </div>
            </div>

            {/* Right side - Content */}
            <div className="space-y-8 order-1 md:order-2 text-center md:text-left">
              <div className="space-y-6">
                {/* Main Headline */}
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                  Save Your Spoons For What Matters. <span className="text-primary">We'll Handle The Rest.</span>
                </h1>

                {/* Subheadline */}
                <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                  From high-stakes decisions to everyday admin - get the thinking support you need to focus your energy
                  on what only you can do.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                {user ? (
                  <Button size="lg" onClick={() => navigate(ROUTES.DASHBOARD)} className="text-lg px-8 py-6 group">
                    Go to Dashboard
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                ) : (
                  <>
                    <Button size="lg" onClick={() => navigate(ROUTES.AUTH)} className="text-lg px-8 py-6 group">
                      Get Started Free
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => navigate(ROUTES.AUTH)}
                      className="text-lg px-8 py-6"
                    >
                      Sign In
                    </Button>
                  </>
                )}
              </div>

              {/* Trust Badge */}
              <p className="text-sm text-muted-foreground">
                Join founders and leaders reclaiming their mental energy every day
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 md:py-24 px-4 md:px-8 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              Thinking Tools For All The Work
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Whether you're navigating a complex challenge or just drowning in daily tasks - we help you reclaim your
              mental energy
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isVisible = visibleCards.has(index);
              return (
                <Card
                  key={index}
                  ref={(el) => {
                    if (el) cardRefs.current[index] = el;
                  }}
                  className={`hover-scale border-border/50 transition-all duration-700 hover:shadow-lg hover:border-primary/20 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{
                    transitionDelay: `${index * 150}ms`,
                  }}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-16 md:py-24 px-4 md:px-8 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">How It Works</h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Get started in minutes and transform how you make decisions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {/* Step 1 */}
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-primary">1</span>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground">Choose Your Support</h3>
              <p className="text-muted-foreground leading-relaxed">
                Pick a pre-built board for common situations, or describe what's overwhelming you and let AI create
                custom thinking support.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-primary">2</span>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground">Work With AI Experts</h3>
              <p className="text-muted-foreground leading-relaxed">
                Delegate the mental overhead to AI personas who help you break down complexity, handle routine tasks,
                and spot what you're missing.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-primary">3</span>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground">Focus On What Matters</h3>
              <p className="text-muted-foreground leading-relaxed">
                Save your spoons for strategic work. Let your AI board handle the admin, track context, and free up your
                mental bandwidth.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="py-16 md:py-24 px-4 md:px-8 bg-background">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              Ready To Save Your Spoons?
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Stop carrying everything in your head. Get AI-powered thinking support for the big decisions and everyday
              overwhelm - so you can focus on what only you can do.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {user ? (
              <Button size="lg" onClick={() => navigate(ROUTES.DASHBOARD)} className="text-lg px-10 py-7 group">
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            ) : (
              <>
                <Button size="lg" onClick={() => navigate(ROUTES.AUTH)} className="text-lg px-10 py-7 group">
                  Start Free Today
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate(ROUTES.AUTH)}
                  className="text-lg px-10 py-7"
                >
                  Sign In
                </Button>
              </>
            )}
          </div>

          <p className="text-sm text-muted-foreground pt-4">No credit card required • Free to get started</p>
        </div>
      </div>
    </div>
  );
};

export default Index;
